import type { ModifierGroupDto, UpsertModifierGroupInput } from '@telestore/contracts';
import type { TranslationKey, TranslationParams } from '@telestore/i18n';
import { pickText } from '@telestore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAmount, toDecimal } from '@telestore/shared';
import { useState } from 'react';
import { Check, GripVertical, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import {
  Button,
  Empty,
  ErrorBox,
  Field,
  Input,
  Panel,
  Row,
  Rows,
  Spinner,
} from '../components/ui.js';

/**
 * F&B menu management (§4).
 *
 * Two jobs, and they belong to different moments of the day. Modifier groups
 * are set up once and edited rarely. The sold-out board is touched mid-service
 * from a phone with one hand, which is why it is a list of big toggles at the
 * top of the page and not a field inside a product form.
 */
export function MenuPage() {
  const { shopId, locale, t } = useSession();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ModifierGroupDto | 'new' | null>(null);

  const groups = useQuery({
    queryKey: ['modifier-groups', shopId],
    enabled: Boolean(shopId),
    queryFn: () => api.modifierGroups(shopId!),
  });

  const soldOut = useQuery({
    queryKey: ['sold-out', shopId],
    enabled: Boolean(shopId),
    queryFn: () => api.soldOut(shopId!),
  });

  const products = useQuery({
    queryKey: ['products', shopId, ''],
    enabled: Boolean(shopId),
    queryFn: () => api.products(shopId!, {}),
  });

  const toggleSoldOut = useMutation({
    mutationFn: (input: { productId: string; isSoldOut: boolean }) =>
      api.setSoldOut(shopId!, input.productId, input.isSoldOut),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sold-out', shopId] });
    },
  });

  const clearAll = useMutation({
    mutationFn: () => api.clearSoldOut(shopId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sold-out', shopId] });
    },
  });

  const remove = useMutation({
    mutationFn: (groupId: string) => api.deleteModifierGroup(shopId!, groupId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['modifier-groups', shopId] });
    },
  });

  if (!shopId) return <Spinner />;

  const soldOutIds = new Set(soldOut.data?.items.map((item) => item.productId) ?? []);
  const menu = products.data?.items ?? [];

  return (
    <div className="flex flex-col gap-5 p-4">
      {/*
        The sold-out board goes first. During service this is the only screen
        the kitchen opens, and it should not have to be scrolled to.
      */}
      <Panel
        title={t('fnb.soldOutBoard')}
        action={
          soldOutIds.size > 0 ? (
            <Button
              variant="outline"
              disabled={clearAll.isPending}
              onClick={() => clearAll.mutate()}
            >
              <RotateCcw size={16} />
              {t('fnb.restockAll')}
            </Button>
          ) : null
        }
      >
        <p className="mb-3 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
          {t('fnb.soldOutHint')}
        </p>

        {products.isPending ? <Spinner /> : null}
        {!products.isPending && menu.length === 0 ? (
          <Empty title={t('common.empty')} />
        ) : null}

        <Rows>
          {menu.map((product) => {
            const off = soldOutIds.has(product.id);
            return (
              <Row key={product.id}>
                <span className={off ? 'line-through opacity-50' : ''}>
                  {pickText(product.name, locale)}
                </span>
                <Button
                  variant={off ? 'outline' : 'ghost'}
                  disabled={toggleSoldOut.isPending}
                  onClick={() =>
                    toggleSoldOut.mutate({ productId: product.id, isSoldOut: !off })
                  }
                >
                  {off ? <Check size={16} /> : <X size={16} />}
                  {off ? t('fnb.markAvailable') : t('fnb.markSoldOut')}
                </Button>
              </Row>
            );
          })}
        </Rows>
      </Panel>

      <Panel
        title={t('fnb.modifierGroups')}
        action={
          <Button onClick={() => setEditing('new')}>
            <Plus size={16} />
            {t('fnb.newGroup')}
          </Button>
        }
      >
        <p className="mb-3 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
          {/* §4: groups attach many-to-many, so "Sugar level" is written once. */}
          {t('fnb.modifierGroupsHint')}
        </p>

        {groups.isPending ? <Spinner /> : null}
        {!groups.isPending && (groups.data?.groups.length ?? 0) === 0 ? (
          <Empty title={t('common.empty')} />
        ) : null}

        <Rows>
          {groups.data?.groups.map((group) => (
            <Row key={group.id} onClick={() => setEditing(group)}>
              <span className="flex items-center gap-2">
                <GripVertical size={14} className="opacity-40" aria-hidden="true" />
                <span>
                  <span className="font-medium">{pickText(group.name, locale)}</span>
                  <span className="ml-2 text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                    {group.options.length} · {ruleLabel(group, t)}
                  </span>
                </span>
              </span>
              <Button
                variant="ghost"
                onClick={(event) => {
                  event.stopPropagation();
                  remove.mutate(group.id);
                }}
              >
                <Trash2 size={16} />
              </Button>
            </Row>
          ))}
        </Rows>
      </Panel>

      {editing ? (
        <GroupEditor
          group={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['modifier-groups', shopId] });
          }}
        />
      ) : null}
    </div>
  );
}

/** "Exactly one" / "Pick up to 3" — the rule in the merchant's own words. */
function ruleLabel(
  group: Pick<ModifierGroupDto, 'selection' | 'minSelect' | 'maxSelect'>,
  t: (key: TranslationKey, params?: TranslationParams) => string,
): string {
  if (group.selection === 'single') {
    return group.minSelect > 0 ? t('fnb.pickOne') : t('common.optional');
  }
  return t('fnb.pickUpTo', { count: group.maxSelect });
}

interface DraftOption {
  id?: string;
  nameKm: string;
  nameEn: string;
  priceDelta: string;
  isDefault: boolean;
  isAvailable: boolean;
}

function GroupEditor({
  group,
  onClose,
  onSaved,
}: {
  group: ModifierGroupDto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { shopId, t } = useSession();
  /**
   * A delta is money, so it needs the shop's currency — a KHR shop types
   * 500, not 5.00, and rounding one as the other is a real price error.
   */
  const shop = useQuery({
    queryKey: ['shop', shopId],
    enabled: Boolean(shopId),
    queryFn: () => api.shop(shopId!),
    staleTime: 5 * 60_000,
  });
  const currency = shop.data?.shop.currency ?? 'USD';
  const [nameKm, setNameKm] = useState(group?.name.km ?? '');
  const [nameEn, setNameEn] = useState(group?.name.en ?? '');
  const [selection, setSelection] = useState<'single' | 'multiple'>(group?.selection ?? 'single');
  /**
   * Required is a checkbox, not a min/max pair. A merchant thinks "they have
   * to choose a size" — not "minSelect = 1" — and every group we have seen in
   * the wild is either required-pick-1 or free-pick-N.
   */
  const [required, setRequired] = useState((group?.minSelect ?? 1) > 0);
  const [maxSelect, setMaxSelect] = useState(String(group?.maxSelect ?? 1));
  const [error, setError] = useState<string | null>(null);

  const [options, setOptions] = useState<DraftOption[]>(
    group?.options.map((option) => ({
      id: option.id,
      nameKm: option.name.km,
      nameEn: option.name.en,
      priceDelta: String(toDecimal({ amount: option.priceDelta, currency })),
      isDefault: option.isDefault,
      isAvailable: option.isAvailable,
    })) ?? [{ nameKm: '', nameEn: '', priceDelta: '0', isDefault: true, isAvailable: true }],
  );

  const save = useMutation({
    mutationFn: () => {
      const body: UpsertModifierGroupInput = {
        name: { km: nameKm.trim() || nameEn.trim(), en: nameEn.trim() || nameKm.trim() },
        selection,
        // Order is the list order; the editor has no separate drag handle yet.
        position: group?.position ?? 0,
        minSelect: required ? 1 : 0,
        maxSelect: selection === 'single' ? 1 : Math.max(1, Number(maxSelect) || 1),
        isActive: true,
        options: options
          .filter((option) => option.nameKm.trim() || option.nameEn.trim())
          .map((option, index) => ({
            ...(option.id ? { id: option.id } : {}),
            name: {
              km: option.nameKm.trim() || option.nameEn.trim(),
              en: option.nameEn.trim() || option.nameKm.trim(),
            },
            /**
             * A delta can be negative — "small size, −$0.25" is a real menu
             * line — so this parses a signed amount rather than a price.
             * Unparseable text becomes no change rather than an exception: a
             * half-typed number should not throw away the whole group.
             */
            priceDelta: safeAmount(option.priceDelta, currency),
            isDefault: option.isDefault,
            isAvailable: option.isAvailable,
            position: index,
          })),
      };
      return group
        ? api.updateModifierGroup(shopId!, group.id, body)
        : api.createModifierGroup(shopId!, body);
    },
    onSuccess: onSaved,
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  function patch(index: number, next: Partial<DraftOption>) {
    setOptions((current) =>
      current.map((option, position) =>
        position === index ? { ...option, ...next } : next.isDefault && selection === 'single'
          ? // A pick-one group has exactly one default.
            { ...option, isDefault: false }
          : option,
      ),
    );
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center sm:items-center"
      style={{ background: 'rgb(0 0 0 / 0.4)' }}
      onClick={onClose}
    >
      <div
        className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl p-4 sm:rounded-2xl"
        style={{ background: 'var(--color-panel)' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">
            {group ? t('common.edit') : t('fnb.newGroup')}
          </h2>
          <Button variant="ghost" onClick={onClose}>
            <X size={16} />
          </Button>
        </div>

        <div className="flex flex-col gap-3">
          <Field label={`${`${t('checkout.name')} (ខ្មែរ)`}`}>
            <Input value={nameKm} onChange={(event) => setNameKm(event.target.value)} />
          </Field>
          <Field label={`${`${t('checkout.name')} (EN)`}`}>
            <Input value={nameEn} onChange={(event) => setNameEn(event.target.value)} />
          </Field>

          <div className="flex flex-wrap gap-2">
            <Toggle
              active={selection === 'single'}
              onClick={() => setSelection('single')}
              label={t('fnb.pickOne')}
            />
            <Toggle
              active={selection === 'multiple'}
              onClick={() => setSelection('multiple')}
              label={t('fnb.pickMany')}
            />
            <Toggle
              active={required}
              onClick={() => setRequired(!required)}
              label={t('common.required')}
            />
          </div>

          {selection === 'multiple' ? (
            <Field label={t('fnb.maxSelect')}>
              <Input
                inputMode="numeric"
                value={maxSelect}
                onChange={(event) => setMaxSelect(event.target.value)}
              />
            </Field>
          ) : null}

          <div>
            <p className="mb-2 text-sm font-medium">{t('fnb.optionName')}</p>
            <div className="flex flex-col gap-2">
              {options.map((option, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-2 rounded-lg p-2"
                  style={{ border: '1px solid var(--color-line)' }}
                >
                  <div className="flex gap-2">
                    <Input
                      placeholder={`${t('checkout.name')} (ខ្មែរ)`}
                      value={option.nameKm}
                      onChange={(event) => patch(index, { nameKm: event.target.value })}
                    />
                    <Input
                      placeholder={`${t('checkout.name')} (EN)`}
                      value={option.nameEn}
                      onChange={(event) => patch(index, { nameEn: event.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      className="max-w-28"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={option.priceDelta}
                      onChange={(event) => patch(index, { priceDelta: event.target.value })}
                    />
                    <Toggle
                      active={option.isDefault}
                      onClick={() => patch(index, { isDefault: !option.isDefault })}
                      label={t('fnb.isDefault')}
                    />
                    <Toggle
                      active={option.isAvailable}
                      onClick={() => patch(index, { isAvailable: !option.isAvailable })}
                      label={t('fnb.isAvailable')}
                    />
                    <Button
                      variant="ghost"
                      onClick={() =>
                        setOptions((current) => current.filter((_, position) => position !== index))
                      }
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              className="mt-2"
              onClick={() =>
                setOptions((current) => [
                  ...current,
                  { nameKm: '', nameEn: '', priceDelta: '0', isDefault: false, isAvailable: true },
                ])
              }
            >
              <Plus size={16} />
              {t('fnb.addOption')}
            </Button>
          </div>

          {error ? <ErrorBox message={error} /> : null}

          <Button
            className="w-full"
            disabled={save.isPending || (!nameKm.trim() && !nameEn.trim())}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** `parseAmount` throws on junk; a half-typed field is not an error here. */
function safeAmount(input: string, currency: 'USD' | 'KHR'): number {
  try {
    return parseAmount(input, currency).amount;
  } catch {
    return 0;
  }
}

function Toggle({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full px-3 py-1.5 text-xs font-medium"
      style={{
        border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-line)'}`,
        background: active ? 'var(--color-accent)' : 'transparent',
        color: active ? 'var(--color-accent-contrast)' : 'var(--color-ink)',
      }}
    >
      {label}
    </button>
  );
}

